/**
 * Session machine — the codified lesson of the 2026-07-15 dead-shell evening.
 * Invariants (each has a test):
 *   1. A saved session NEVER blocks painting. Auth repair runs in background.
 *   2. Only an explicit Supabase rejection (400/401/403 on refresh) logs out.
 *   3. Refresh is single-flight; v2 uses its OWN session (crm2_session_v1) so
 *      it can never rotate/revoke the v1 app's refresh-token family.
 *   4. Every transition is observable (subscribe) — no silent states.
 *   5. The machine RE-ARMS: after settling `authed` it schedules its own
 *      refresh ~2 min before expiry, and token() self-triggers a repair when
 *      it hands out a stale token (found live 2026-07-17: a tab open >1h had
 *      an expired token and every backend call failed with invalid_token —
 *      boot-only repair was not enough).
 */
import { http, HttpError } from "./http";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "../config";

export interface Session {
  access_token: string;
  refresh_token: string;
  /** epoch ms */
  expires_at: number;
  user: { id: string; email?: string };
}

export type AuthState =
  | { kind: "no-session" }
  | { kind: "repairing"; session: Session; sinceMs: number } // painted, fixing in bg
  | { kind: "authed"; session: Session }
  | { kind: "signed-out"; reason: string };

const SKEY = "crm2_session_v1";

type Listener = (s: AuthState) => void;

export class Auth {
  private state: AuthState;
  private listeners = new Set<Listener>();
  private refreshing: Promise<Session | null> | null = null;
  private stopped = false;
  private armTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private storage: Pick<Storage, "getItem" | "setItem" | "removeItem">) {
    const saved = this.load();
    this.state = saved ? { kind: "repairing", session: saved, sinceMs: Date.now() } : { kind: "no-session" };
  }

  get current(): AuthState {
    return this.state;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => this.listeners.delete(fn);
  }

  private set(next: AuthState) {
    this.state = next;
    for (const fn of this.listeners) fn(next);
  }

  private load(): Session | null {
    try {
      const s = JSON.parse(this.storage.getItem(SKEY) ?? "null") as Session | null;
      return s?.access_token && s?.refresh_token ? s : null;
    } catch {
      return null;
    }
  }

  private save(s: Session) {
    this.storage.setItem(SKEY, JSON.stringify(s));
  }

  /** Background repair loop: retries forever on network trouble, signs out
   * ONLY on definitive rejection. Callers paint immediately; this catches up.
   * Every `authed` settlement re-arms a proactive refresh (invariant 5). */
  async repair(): Promise<void> {
    let round = 0;
    while (!this.stopped) {
      const s = this.load();
      if (!s) {
        this.set({ kind: "no-session" });
        return;
      }
      // fresh enough = strictly MORE than the arm target (T-2min), so the
      // armed wake-up always lands in the refresh branch — no 5s churn loop
      if (s.expires_at > Date.now() + 150000) {
        this.set({ kind: "authed", session: s });
        this.arm(s);
        return;
      }
      const fresh = await this.refreshOnce(s.refresh_token);
      if (fresh) {
        this.set({ kind: "authed", session: fresh });
        this.arm(fresh);
        return;
      }
      if (this.definitiveReject) {
        // Multi-tab rescue (2026-07-22 audit, R2): Supabase ROTATES refresh
        // tokens, so two tabs racing the same refresh make the loser see a
        // definitive 400 (token reuse) — while the WINNER just saved a fresh
        // session to this same storage. Re-read before giving up: if another
        // tab rotated successfully, adopt its session instead of signing
        // everyone out. Only a real rejection (no fresher session anywhere)
        // still signs out.
        const rescued = this.load();
        if (rescued && rescued.refresh_token !== s.refresh_token && rescued.expires_at > Date.now() + 150000) {
          this.set({ kind: "authed", session: rescued });
          this.arm(rescued);
          return;
        }
        this.storage.removeItem(SKEY);
        this.set({ kind: "signed-out", reason: "Your session expired. Please sign in again." });
        return;
      }
      this.set({ kind: "repairing", session: s, sinceMs: this.state.kind === "repairing" ? this.state.sinceMs : Date.now() });
      await new Promise((r) => setTimeout(r, Math.min(30000, 2000 * ++round)));
    }
  }

  /** Schedule the NEXT refresh ~2 min before this session expires. A sleeping
   * laptop pauses timers — the late firing still repairs on wake, and token()
   * below covers any call that beats it. */
  private arm(s: Session) {
    if (this.armTimer) clearTimeout(this.armTimer);
    if (this.stopped) return;
    const delay = Math.max(5000, s.expires_at - Date.now() - 120000);
    this.armTimer = setTimeout(() => {
      this.armTimer = null;
      void this.repair();
    }, delay);
  }

  private definitiveReject = false;

  /** single-flight refresh */
  private refreshOnce(rt: string): Promise<Session | null> {
    if (!this.refreshing) {
      this.definitiveReject = false;
      this.refreshing = (async () => {
        try {
          const d = await http<{ access_token: string; refresh_token: string; expires_in?: number; user: Session["user"] }>(
            `${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`,
            { method: "POST", headers: { apikey: SUPABASE_ANON_KEY }, body: { refresh_token: rt }, timeoutMs: 12000 },
          );
          const s: Session = {
            access_token: d.access_token,
            refresh_token: d.refresh_token,
            expires_at: Date.now() + (d.expires_in ?? 3600) * 1000,
            user: d.user,
          };
          this.save(s);
          return s;
        } catch (e) {
          if (e instanceof HttpError && [400, 401, 403].includes(e.status)) this.definitiveReject = true;
          return null;
        } finally {
          this.refreshing = null;
        }
      })();
    }
    return this.refreshing;
  }

  async signIn(email: string, password: string): Promise<{ ok: true } | { ok: false; message: string }> {
    try {
      const d = await http<{ access_token: string; refresh_token: string; expires_in?: number; user: Session["user"] }>(
        `${SUPABASE_URL}/auth/v1/token?grant_type=password`,
        { method: "POST", headers: { apikey: SUPABASE_ANON_KEY }, body: { email, password }, timeoutMs: 15000 },
      );
      const s: Session = {
        access_token: d.access_token,
        refresh_token: d.refresh_token,
        expires_at: Date.now() + (d.expires_in ?? 3600) * 1000,
        user: d.user,
      };
      this.save(s);
      this.set({ kind: "authed", session: s });
      return { ok: true };
    } catch (e) {
      if (e instanceof HttpError) {
        const b = e.body as { error_description?: string; msg?: string } | null;
        return { ok: false, message: b?.error_description ?? b?.msg ?? "Invalid email or password." };
      }
      return { ok: false, message: "Can't reach the sign-in server — your connection or the server is down. Try again in a minute." };
    }
  }

  signOut() {
    this.storage.removeItem(SKEY);
    this.set({ kind: "signed-out", reason: "Signed out." });
  }

  /** current access token for REST calls (may be stale mid-repair — RLS just
   * rejects, the repair loop replaces it, callers retry via their own layer).
   * Handing out a stale token also KICKS a repair, so the next call heals. */
  token(): string | null {
    const s = this.load();
    if (s && s.expires_at <= Date.now() + 60000 && !this.refreshing && !this.stopped) {
      void this.repair();
    }
    return s?.access_token ?? null;
  }

  stop() {
    this.stopped = true;
    if (this.armTimer) {
      clearTimeout(this.armTimer);
      this.armTimer = null;
    }
  }
}
